import React, { useState, useEffect } from "react";
import { Container, Form, Button, Card, InputGroup, Alert } from "react-bootstrap";
import { BsEyeSlash, BsEye, BsArrowLeft } from "react-icons/bs";
import { Link } from "react-router-dom";
import {
    forgotPassword,
    confirmForgotPassword,
    findUsernameByEmail
} from "../API/CognitoAdminApi";
import "../../styles/LogIn.css";

const ForgotPassword = () => {
    // Tab state: 'password' or 'username'
    const [activeTab, setActiveTab] = useState("password");

    // Forgot Password states
    const [username, setUsername] = useState("");
    const [verificationCode, setVerificationCode] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [step, setStep] = useState(1); // 1: Enter username, 2: Enter code & new password

    // Forgot Username states
    const [email, setEmail] = useState("");
    const [foundUsername, setFoundUsername] = useState("");

    // Common states
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    // Password validation
    const [passwordValidation, setPasswordValidation] = useState({
        minLength: false,
        hasUppercase: false,
        hasLowercase: false,
        hasNumber: false,
        hasSpecial: false
    });

    useEffect(() => {
        setPasswordValidation({
            minLength: newPassword.length >= 8,
            hasUppercase: /[A-Z]/.test(newPassword),
            hasLowercase: /[a-z]/.test(newPassword),
            hasNumber: /[0-9]/.test(newPassword),
            hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword)
        });
    }, [newPassword]);

    const resetStates = () => {
        setUsername("");
        setVerificationCode("");
        setNewPassword("");
        setConfirmPassword("");
        setEmail("");
        setFoundUsername("");
        setStep(1);
        setError("");
        setSuccess("");
    };

    const handleTabChange = (tab) => {
        resetStates();
        setActiveTab(tab);
    };

    // Handle forgot password - Step 1: Send verification code
    const handleSendCode = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        if (!username.trim()) {
            setError("Please enter your username");
            return;
        }

        setIsLoading(true);

        try {
            const result = await forgotPassword(username);
            if (result.success) {
                setSuccess("Verification code has been sent to your email address");
                setStep(2);
            }
        } catch (err) {
            setError(err.error || "Failed to send verification code. Please check your username.");
        } finally {
            setIsLoading(false);
        }
    };

    // Handle forgot password - Step 2: Reset password
    const handleResetPassword = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        if (!verificationCode.trim()) {
            setError("Please enter the verification code");
            return;
        }

        const allPasswordReqsMet = Object.values(passwordValidation).every(v => v);
        if (!allPasswordReqsMet) {
            setError("Password does not meet all requirements");
            return;
        }

        if (newPassword !== confirmPassword) {
            setError("Passwords do not match");
            return;
        }

        setIsLoading(true);

        try {
            const result = await confirmForgotPassword(username, verificationCode, newPassword);
            if (result.success) {
                setSuccess("Password reset successfully! You can now login with your new password.");
                setStep(1);
                setUsername("");
                setVerificationCode("");
                setNewPassword("");
                setConfirmPassword("");
            }
        } catch (err) {
            setError(err.error || "Failed to reset password. Please check the verification code.");
        } finally {
            setIsLoading(false);
        }
    };

    // Handle forgot username
    const handleFindUsername = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");
        setFoundUsername("");

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email.trim() || !emailRegex.test(email)) {
            setError("Please enter a valid email address");
            return;
        }

        setIsLoading(true);

        try {
            const result = await findUsernameByEmail(email);
            if (result.success) {
                setFoundUsername(result.username);
                setSuccess(`Your username is: ${result.username}`);
            } else {
                setError(result.error || "No user found with this email address");
            }
        } catch (err) {
            setError("Failed to find username. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const PasswordRequirement = ({ met, text }) => (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            fontSize: '0.8rem',
            color: met ? '#28a745' : '#6c757d',
            marginBottom: '2px'
        }}>
            <span style={{ marginRight: '5px' }}>{met ? '✓' : '○'}</span>
            <span>{text}</span>
        </div>
    );

    return (
        <Container className="d-flex justify-content-center align-items-center login-container" style={{ minHeight: "100vh" }}>
            <Card className="login-card shadow-sm" style={{ maxWidth: "500px", width: "100%" }}>
                <Card.Body className="p-4">
                    {/* Header */}
                    <div className="text-center mb-4">
                        <img
                            src="./Suhana_Logo.jpg"
                            alt="Suhana Logo"
                            className="mb-3"
                            style={{ height: "70px", maxWidth: "200px" }}
                        />
                        <h4 className="mb-3">Account Recovery</h4>
                    </div>

                    {/* Tab Navigation */}
                    <div className="d-flex mb-4" style={{ borderBottom: '2px solid #dee2e6' }}>
                        <button
                            type="button"
                            onClick={() => handleTabChange("password")}
                            style={{
                                flex: 1,
                                padding: '10px',
                                border: 'none',
                                background: 'none',
                                borderBottom: activeTab === 'password' ? '2px solid #0d6efd' : 'none',
                                color: activeTab === 'password' ? '#0d6efd' : '#6c757d',
                                fontWeight: activeTab === 'password' ? '600' : '400',
                                cursor: 'pointer',
                                marginBottom: '-2px'
                            }}
                        >
                            Forgot Password
                        </button>
                        <button
                            type="button"
                            onClick={() => handleTabChange("username")}
                            style={{
                                flex: 1,
                                padding: '10px',
                                border: 'none',
                                background: 'none',
                                borderBottom: activeTab === 'username' ? '2px solid #0d6efd' : 'none',
                                color: activeTab === 'username' ? '#0d6efd' : '#6c757d',
                                fontWeight: activeTab === 'username' ? '600' : '400',
                                cursor: 'pointer',
                                marginBottom: '-2px'
                            }}
                        >
                            Forgot Username
                        </button>
                    </div>

                    {/* Alerts */}
                    {error && (
                        <Alert variant="danger" onClose={() => setError("")} dismissible>
                            {error}
                        </Alert>
                    )}
                    {success && (
                        <Alert variant="success" onClose={() => setSuccess("")} dismissible>
                            {success}
                        </Alert>
                    )}

                    {/* Forgot Password Tab */}
                    {activeTab === "password" && (
                        <>
                            {step === 1 ? (
                                <Form onSubmit={handleSendCode}>
                                    <p className="text-muted mb-3">
                                        Enter your username and we'll send a verification code to your registered email.
                                    </p>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Username</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter your username"
                                            value={username}
                                            onChange={(e) => setUsername(e.target.value)}
                                            required
                                            disabled={isLoading}
                                        />
                                    </Form.Group>

                                    <Button
                                        variant="primary"
                                        type="submit"
                                        className="w-100 login-btn mb-3"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? "Sending..." : "Send Verification Code"}
                                    </Button>
                                </Form>
                            ) : (
                                <Form onSubmit={handleResetPassword}>
                                    <p className="text-muted mb-3">
                                        Enter the verification code sent to your email and your new password.
                                    </p>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Verification Code</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter verification code"
                                            value={verificationCode}
                                            onChange={(e) => setVerificationCode(e.target.value)}
                                            required
                                            disabled={isLoading}
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>New Password</Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type={showNewPassword ? "text" : "password"}
                                                placeholder="Enter new password"
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                required
                                                disabled={isLoading}
                                            />
                                            <Button
                                                variant="outline-secondary"
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                            >
                                                {showNewPassword ? <BsEyeSlash /> : <BsEye />}
                                            </Button>
                                        </InputGroup>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Confirm New Password</Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type={showConfirmPassword ? "text" : "password"}
                                                placeholder="Confirm new password"
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                required
                                                disabled={isLoading}
                                            />
                                            <Button
                                                variant="outline-secondary"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            >
                                                {showConfirmPassword ? <BsEyeSlash /> : <BsEye />}
                                            </Button>
                                        </InputGroup>
                                    </Form.Group>

                                    {/* Password Requirements */}
                                    <div className="card mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                                        <div className="card-body py-2">
                                            <small className="text-muted d-block mb-2">Password Requirements:</small>
                                            <div className="row">
                                                <div className="col-6">
                                                    <PasswordRequirement met={passwordValidation.minLength} text="At least 8 characters" />
                                                    <PasswordRequirement met={passwordValidation.hasUppercase} text="One uppercase letter" />
                                                    <PasswordRequirement met={passwordValidation.hasLowercase} text="One lowercase letter" />
                                                </div>
                                                <div className="col-6">
                                                    <PasswordRequirement met={passwordValidation.hasNumber} text="One number" />
                                                    <PasswordRequirement met={passwordValidation.hasSpecial} text="One special character" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <Button
                                        variant="primary"
                                        type="submit"
                                        className="w-100 login-btn mb-3"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? "Resetting..." : "Reset Password"}
                                    </Button>

                                    <Button
                                        variant="outline-secondary"
                                        className="w-100"
                                        onClick={() => {
                                            setStep(1);
                                            setVerificationCode("");
                                            setNewPassword("");
                                            setConfirmPassword("");
                                            setError("");
                                        }}
                                        disabled={isLoading}
                                    >
                                        <BsArrowLeft className="me-2" />
                                        Back to Username
                                    </Button>
                                </Form>
                            )}
                        </>
                    )}

                    {/* Forgot Username Tab */}
                    {activeTab === "username" && (
                        <Form onSubmit={handleFindUsername}>
                            <p className="text-muted mb-3">
                                Enter your registered email address to find your username.
                            </p>
                            <Form.Group className="mb-3">
                                <Form.Label>Email Address</Form.Label>
                                <Form.Control
                                    type="email"
                                    placeholder="Enter your email address"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    disabled={isLoading}
                                />
                            </Form.Group>

                            {foundUsername && (
                                <div className="alert alert-info mb-3">
                                    <strong>Your username is:</strong> {foundUsername}
                                </div>
                            )}

                            <Button
                                variant="primary"
                                type="submit"
                                className="w-100 login-btn mb-3"
                                disabled={isLoading}
                            >
                                {isLoading ? "Searching..." : "Find My Username"}
                            </Button>
                        </Form>
                    )}

                    {/* Back to Login Link */}
                    <div className="text-center mt-3">
                        <Link to="/login" className="text-decoration-none">
                            <BsArrowLeft className="me-1" />
                            Back to Login
                        </Link>
                    </div>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default ForgotPassword;
